import { Link } from 'react-router';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { parseTimestamp } from '@/utils/datetime';
import {
  useAuditCatalog,
  useSeedDefaultAudits,
  type AuditCatalogEntry,
} from '../../hooks/useAuditCatalog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

function severityStyle(severity: string): string {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'bg-sev-1/15 text-sev-1';
    case 'medium':
      return 'bg-sev-3/15 text-foreground';
    case 'low':
      return 'bg-sev-5/10 text-sev-5';
    case 'info':
      return 'bg-sev-5/15 text-sev-5';
    default:
      // Only reached by a severity outside the canonical five, where grey is the
      // honest answer. `low` used to land here, which made a real severity look
      // like an unrecognised one.
      return 'bg-muted text-muted-foreground';
  }
}

function formatInterval(seconds: number): string {
  if (seconds % 86400 === 0) return `${String(seconds / 86400)}d`;
  if (seconds % 3600 === 0) return `${String(seconds / 3600)}h`;
  if (seconds % 60 === 0) return `${String(seconds / 60)}m`;
  return `${String(seconds)}s`;
}

function formatLastRun(iso: string | null): string {
  const date = parseTimestamp(iso);
  if (!date) return 'never';
  return date.toLocaleString();
}

function StatusCell({ entry }: { entry: AuditCatalogEntry }) {
  const task = entry.alertDefinition;
  if (!task) {
    return (
      <Badge variant="secondary" className="font-normal">
        Not scheduled
      </Badge>
    );
  }
  if (!task.enabled) {
    return (
      <Badge variant="secondary" className="font-normal">
        Disabled
      </Badge>
    );
  }
  if (task.alertState === 'alerting') {
    return (
      <Badge className="bg-sev-1/15 text-sev-1 border-0 font-normal capitalize">
        {task.alertState}
      </Badge>
    );
  }
  return (
    <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal capitalize">
      {task.alertState}
    </Badge>
  );
}

export default function AuditCatalog() {
  const { data: entries, isLoading, isError } = useAuditCatalog();
  const seedMutation = useSeedDefaultAudits();

  if (isLoading) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">Loading audit catalog…</p>
      </PageContainer>
    );
  }
  if (isError || !entries) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load audit catalog.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const scheduledCount = entries.filter((e) => e.alertDefinition !== null).length;
  const enabledCount = entries.filter((e) => e.alertDefinition?.enabled).length;

  const columns: readonly ResponsiveTableColumn<AuditCatalogEntry>[] = [
    {
      key: 'audit',
      label: 'Audit',
      hideMobileLabel: true,
      render: (entry) => (
        <div>
          <div className="font-semibold">{entry.displayName}</div>
          <p className="text-xs text-muted-foreground">{entry.description}</p>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type ID',
      render: (entry) => <code className="font-mono text-xs">{entry.auditType}</code>,
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (entry) => {
        const severity = entry.alertDefinition
          ? entry.alertDefinition.severity
          : entry.defaultSeverity;
        return (
          <Badge className={cn('border-0 font-normal capitalize', severityStyle(severity))}>
            {severity}
          </Badge>
        );
      },
    },
    {
      key: 'interval',
      label: 'Interval',
      render: (entry) => (
        <span className="text-xs">
          {formatInterval(
            entry.alertDefinition
              ? entry.alertDefinition.intervalSeconds
              : entry.defaultIntervalSeconds,
          )}
        </span>
      ),
    },
    {
      key: 'state',
      label: 'State',
      render: (entry) => <StatusCell entry={entry} />,
    },
    {
      key: 'lastRun',
      label: 'Last run',
      hideAt: 'mobile',
      render: (entry) => (
        <span className="text-xs text-muted-foreground">
          {entry.alertDefinition ? formatLastRun(entry.alertDefinition.lastRunAt) : '—'}
        </span>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Audit catalog"
        subtitle={`${String(scheduledCount)} of ${String(entries.length)} audits scheduled · ${String(enabledCount)} enabled`}
        primary={
          <Button
            onClick={() => { seedMutation.mutate(); }}
            disabled={seedMutation.isPending}
            title="Create default alert-definition rows for any audit type missing one"
          >
            {seedMutation.isPending ? 'Seeding…' : 'Seed missing defaults'}
          </Button>
        }
      />

      {seedMutation.isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to seed defaults. See server logs for details.
          </AlertDescription>
        </Alert>
      ) : null}
      {seedMutation.isSuccess ? (
        <Alert className="mb-4 border-sev-4/40 bg-sev-4/10">
          <CheckCircle2 className="h-4 w-4 text-sev-4" />
          <AlertDescription>
            Seed complete. Any missing default audits have been added.
          </AlertDescription>
        </Alert>
      ) : null}

      <ResponsiveTable<AuditCatalogEntry>
        columns={columns}
        rows={entries}
        rowKey={(e) => e.auditType}
        rowActions={(entry) =>
          entry.alertDefinition ? (
            <Button asChild variant="outline" size="sm">
              <Link to={`/alerts?edit=${entry.alertDefinition.taskId}`}>Configure</Link>
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">use Seed defaults</span>
          )
        }
        empty={<EmptyState variant="no-data" title="No audits in the catalog" />}
      />
    </PageContainer>
  );
}
